import React from 'react';

import { Breadcrumbs, ClanNavigation, Modal, Pagination, View } from '@/components';
import { useTranslation } from 'react-i18next';
import { useModal, useProfile } from '@/hooks';
import { CreateClanView } from './create-clan';
import { ClansList } from '@/views/clans/clans-list';
import { useSearchParamsAsObject } from '@/hooks/useSearchParamsAsObject.hook';
import { useClanControllerFindAllQuery } from '@/store/services/MidleNestApi.service';

interface Props {
  mode?: string;
}

export default function ClansView({ mode }: Props /* props: GuildsViewProps */) {
  const { t } = useTranslation();
  const {
    profile: { clan },
  } = useProfile();
  const { showModal } = useModal('create-clan');

  const [search, setSearch] = React.useState<string | undefined>();

  const { searchParams, setSearchParams } = useSearchParamsAsObject<{
    page: number;
    limit: number;
  }>();
  const { page = 1, limit = 10 } = searchParams;

  const { data, isLoading, isFetching } = useClanControllerFindAllQuery(
    { page, limit, search, status: mode },
    // { refetchOnMountOrArgChange: true },
  );

  const onSearchHandler = React.useCallback(
    (value: string) => {
      if (!value || value.length < 1) {
        setSearch(undefined);
        return;
      }

      setSearchParams({ page: '1' });
      setSearch(value);
    },
    [data, search, setSearch],
  );

  return (
    <View isLoading={isLoading}>
      <div className='clans max-w-7xl px-4 py-8 mx-auto space-y-4 xl:space-y-6'>
        <Breadcrumbs paths={[{ href: '/clans', text: t('Clans') }]} />
        <div className='flex flex-col gap-y-8 p-4 lg:p-8 w-full bg-white dark:bg-[#242424] dark:border dark:border-[#FFDA68]/30 shadow-sm rounded-3xl'>
          <div className='flex flex-col space-y-4 lg:space-y-0 lg:flex-row w-full justify-between items-center'>
            <h2 className='flex gap-x-2 items-center text-xl leading-7 font-bold text-gray-900 dark:text-white'>
              <span>{t('All Clans')}</span>
            </h2>
            <ClanNavigation onSearch={onSearchHandler} joinRequests={!clan} invites={!clan} />
          </div>

          <table className='min-w-full dark:text-white'>
            <thead>
              <tr className='text-center text-sm font-semibold'>
                <td className='hidden sm:table-cell sm:px-3' />
                <td className='text-left'>{t('Clan Name')}</td>
                <td className='hidden md:table-cell'>{t('Activity Rating')}</td>
                <td className='hidden sm:table-cell'>{t('Avg. Points Per Day')}</td>
                <td className=''>{t('Total Points')}</td>
                <td className='hidden sm:table-cell'>{t('Members')}</td>
                <td>
                  {!clan && (
                    <button
                      type='button'
                      onClick={() => showModal()}
                      className='pr-3 pb-1 rounded-lg text-white text-sm bg-blue-500 transition-colors hover:bg-blue-500 my-4 dark:bg-[#FFDA68] dark:hover:bg-[#FFDA68] dark:hover:brightness-75 dark:text-[#151515]'
                    >
                      <div className='flex items-center -mt-1'>
                        <span className='icon-add text-2xl mt-2' />
                        <div className='mt-2'>{t('Create Clan')}</div>
                      </div>
                    </button>
                  )}
                </td>
              </tr>
              {/* <TableLoader isLoading={isFetching} /> */}
            </thead>
            <tbody className='divide-y divide-gray-200'>
              {data?.list?.map((i, k) => (
                <ClansList {...i} order={k} key={k} page={page} limit={limit} mode={mode} />
              ))}
            </tbody>
          </table>

          <Pagination page={page} limit={limit} total={data?.meta?.total} />
        </div>

        <Modal id='create-clan' containerCls='w-full'>
          <CreateClanView />
        </Modal>
      </div>
    </View>
  );
}
